import { FileSearch, Files, CheckCircle, XCircle, SkipForward, HelpCircle } from "lucide-react";
import { WebScrapeJob } from "../../../services/web-scraper.service";

interface JobStatsBarProps {
  job: WebScrapeJob;
  skippedCount: number;
}

interface StatEntry {
  label: string;
  value: number;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bg: string;
}

const stats = (job: WebScrapeJob, skippedCount: number): StatEntry[] => [
  {
    label: "Pages visited",
    value: job.pages_crawled ?? 0,
    hint: "How many web pages the crawler has loaded and read so far.",
    icon: FileSearch,
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  {
    label: "Documents found",
    value: job.files_discovered ?? 0,
    hint: "Files (PDFs, Word docs, spreadsheets, images) that the crawler spotted on those pages. Not imported yet.",
    icon: Files,
    color: "text-purple-600",
    bg: "bg-purple-50",
  },
  {
    label: "Imported",
    value: job.items_imported ?? 0,
    hint: "Documents already brought into your library and processed.",
    icon: CheckCircle,
    color: "text-green-600",
    bg: "bg-green-50",
  },
  {
    label: "Skipped",
    value: skippedCount,
    hint: "Total skipped — items you manually skipped from the Discovered Items table plus URLs the crawler dropped (external links, filtered file types, beyond depth limit, etc.). See the Skipped URLs tab for the crawler list.",
    icon: SkipForward,
    color: "text-yellow-600",
    bg: "bg-yellow-50",
  },
  {
    label: "Failed",
    value: job.files_failed ?? 0,
    hint: "Files the crawler tried to download but couldn't (404, timeout, server error).",
    icon: XCircle,
    color: "text-red-600",
    bg: "bg-red-50",
  },
];

export default function JobStatsBar({ job, skippedCount }: JobStatsBarProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
      {stats(job, skippedCount).map(({ label, value, hint, icon: Icon, color, bg }) => (
        <div
          key={label}
          className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg px-4 py-4 relative group"
        >
          <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <p className="text-sm text-gray-500 truncate">{label}</p>
              <div className="relative">
                <HelpCircle className="w-3 h-3 text-gray-300 group-hover:text-gray-400 cursor-help" />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-5 z-10 hidden group-hover:block w-56 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl leading-snug pointer-events-none">
                  {hint}
                </div>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
